import { Box, Skeleton, type BoxProps } from '@chakra-ui/react';
import type { DotLottie, DotLottieReactProps } from '@lottiefiles/dotlottie-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { EmojiSvgRender } from './svg-render';

export interface LottieRenderOptions extends DotLottieReactProps {
  clickPlay?: boolean;
  hoverPlay?: boolean;
}

export interface LottieRenderProps extends BoxProps {
  src?: string;
  getData?: () => Promise<object | undefined>;
  size?: BoxProps['width'];
  emoji?: string;
  lottieOptions?: LottieRenderOptions;
}

export const LottieRender = memo(function EmojiAnimationLottie({
  src,
  getData,
  size = '1em',
  emoji,
  lottieOptions,
}: LottieRenderProps) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const dotLottieRef = useRef<DotLottie>(null);

  const { clickPlay, hoverPlay, ...lottieOptions_ } = useMemo<LottieRenderOptions>(() => {
    return {
      width: '100%',
      height: '100%',
      autoplay: false,
      loop: false,
      ...lottieOptions,
    };
  }, [lottieOptions]);

  const cursor = useMemo(() => {
    return data && clickPlay ? 'pointer' : 'default';
  }, [data, clickPlay]);

  const children = useMemo(() => {
    if (loading) {
      return <Skeleton width="100%" height="100%" borderRadius="lg" />;
    }

    if (data) {
      return (
        <DotLottieReact
          data={data}
          {...lottieOptions_}
          dotLottieRefCallback={(dotLottie) => {
            dotLottieRef.current = dotLottie;
          }}
        />
      );
    }

    return emoji ? <EmojiSvgRender size={'100%'} emoji={emoji} /> : null;
  }, [data, emoji, loading, lottieOptions_]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        setLoading(true);
        let data = null;
        if (src) {
          data = await fetch(src).then((res) => res.json());
        } else if (getData) {
          data = await getData();
        }
        if (data) {
          setData(data);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [src, getData]);

  return (
    <Box
      width={size}
      height={size}
      cursor={cursor}
      onClick={() => {
        if (clickPlay && dotLottieRef.current) {
          dotLottieRef.current.play();
        }
      }}
      onMouseEnter={() => {
        if (hoverPlay && dotLottieRef.current) {
          dotLottieRef.current.setFrame(0);
          dotLottieRef.current.play();
        }
      }}
      onMouseLeave={() => {
        if (hoverPlay && dotLottieRef.current) {
          dotLottieRef.current.setFrame(0);
          dotLottieRef.current.stop();
        }
      }}
    >
      {children}
    </Box>
  );
});
